import { useState, useEffect } from 'react';
import API from '../../api/axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import AppLayout from '../../components/layout/AppLayout';
import { StatCard } from '../../components/ui';
import { S } from '../../components/pageStyles';
import { LuCircleCheck, LuHourglass, LuPackage, LuReceipt, LuShoppingCart, LuStore, LuUser, LuWallet } from 'react-icons/lu';

import { fmtF, fmtK } from '../../theme';
const COULEURS = ['#059669', '#2563EB', '#EA580C', '#D81B60', '#7C3AED', '#0E7490', '#E65100', '#455A64', '#F9A825', '#C62828'];

export default function Rapports() {
  const [chargement, setChargement] = useState(true);
  const [periode, setPeriode] = useState('mois');
  const [etablissementId, setEtablissementId] = useState('');
  const [etablissements, setEtablissements] = useState([]);

  // Données
  const [caData, setCaData] = useState([]);
  const [topProduits, setTopProduits] = useState([]);
  const [ventesServeur, setVentesServeur] = useState([]);
  const [ventesEtab, setVentesEtab] = useState([]);
  const [stockAlertes, setStockAlertes] = useState([]);
  const [resume, setResume] = useState({
    ca_total: 0, nombre_ventes: 0,
    panier_moyen: 0, produits_en_alerte: 0
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    chargerEtablissements();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    chargerRapports();
  }, [periode, etablissementId]);

  const chargerEtablissements = async () => {
    try {
      const res = await API.get('/etablissements');
      setEtablissements(res.data.etablissements || []);
    } catch (err) {
      console.error(err);
    }
  };

  const chargerRapports = async () => {
    setChargement(true);
    try {
      const params = '?periode=' + periode + (etablissementId ? '&etablissement_id=' + etablissementId : '');

      const [caRes, produitsRes, serveurRes, etabRes, alertesRes] = await Promise.all([
        API.get('/rapports/chiffre-affaires' + params),
        API.get('/rapports/top-produits' + params),
        API.get('/rapports/serveurs' + params),
        API.get('/rapports/etablissements' + params),
        API.get('/rapports/stock')
      ]);

      setCaData(caRes.data.data || []);
      setTopProduits(produitsRes.data.produits || []);
      setVentesServeur(serveurRes.data.serveurs || []);
      setVentesEtab(etabRes.data.etablissements || []);
      setStockAlertes(alertesRes.data.produits || []);
      setResume(caRes.data.resume || {
        ca_total: 0, nombre_ventes: 0,
        panier_moyen: 0, produits_en_alerte: 0
      });
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  const formatMontant = (v) => fmtF(parseInt(v || 0));

  return (
    <AppLayout role="admin">

      {/* CONTENU */}
      <div style={styles.contenu}>

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Rapports</h1>
            <p style={styles.headerSousTitre}>Analyse des performances</p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', width: '100%', maxWidth: 680, justifyContent: 'flex-end' }}>
            {/* FILTRE ÉTABLISSEMENT */}
            <select
              value={etablissementId}
              onChange={(e) => setEtablissementId(e.target.value)}
              style={styles.filtreSelect}
            >
              <option value="">Tous les établissements</option>
              {etablissements.map(e => (
                <option key={e.id} value={e.id}>{e.nom}</option>
              ))}
            </select>

            {/* FILTRE PÉRIODE */}
            <div style={styles.periodeBtns}>
              {[
                { val: 'jour', label: 'Jour' },
                { val: 'semaine', label: 'Semaine' },
                { val: 'mois', label: 'Mois' },
                { val: 'annee', label: 'Année' },
              ].map(p => (
                <button
                  key={p.val}
                  onClick={() => setPeriode(p.val)}
                  style={{
                    ...styles.periodeBtn,
                    background: periode === p.val ? '#0F172A' : 'white',
                    color: periode === p.val ? 'white' : '#475569'
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {chargement ? (
          <div style={styles.chargementInline}><LuHourglass aria-hidden style={{ verticalAlign: '-0.125em' }} /> Chargement des données...</div>
        ) : (
          <>
            {/* CARDS RÉSUMÉ */}
            <div style={styles.resumeGrid}>
              {[
                { icon: <LuWallet aria-hidden />, label: 'Revenus totaux', valeur: formatMontant(resume.ca_total), couleur: '#059669', bg: '#ECFDF5' },
                { icon: <LuShoppingCart aria-hidden />, label: 'Nb Ventes', valeur: resume.nombre_ventes || 0, couleur: '#2563EB', bg: '#EFF6FF' },
                { icon: <LuReceipt aria-hidden />, label: 'Panier Moyen', valeur: formatMontant(resume.panier_moyen), couleur: '#EA580C', bg: '#FFF7ED' },
                { icon: <LuPackage aria-hidden />, label: 'Alertes Stock', valeur: stockAlertes.length, couleur: '#DC2626', bg: '#FEF2F2' },
              ].map((card, i) => (
                <StatCard key={i} icon={card.icon} label={card.label} value={card.valeur} color={card.couleur} />
              ))}
            </div>

            {/* GRAPHIQUE CA */}
            <div style={styles.graphCard}>
              <h2 style={styles.graphTitre}><LuWallet aria-hidden style={{ verticalAlign: '-0.125em' }} /> Revenus</h2>
              {caData.length === 0 ? (
                <div style={styles.vide}>Aucune donnée pour cette période</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>

                  <LineChart data={caData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} width={52} />
                    
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'Nb Ventes') return [value, 'Nb Ventes'];
                        return [fmtF(parseInt(value)), 'Revenus'];
                      }}
                    />

                    <Legend />
                    <Line type="monotone" dataKey="ca" stroke="#059669" strokeWidth={3}
                      dot={{ fill: '#059669', r: 5 }} name="Revenus (FCFA)" />
                    <Line type="monotone" dataKey="nombre_ventes" stroke="#2563EB"
                      strokeWidth={2} dot={{ fill: '#2563EB', r: 4 }} name="Nb Ventes" />
                  </LineChart>
                  
                </ResponsiveContainer>
              )}
            </div>

            {/* LIGNE 2 : TOP PRODUITS + VENTES PAR SERVEUR */}
            <div style={styles.deuxColonnes}>

              {/* TOP 10 PRODUITS */}
              <div style={styles.graphCard}>
                <h2 style={styles.graphTitre}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Top 10 Produits</h2>
                {topProduits.length === 0 ? (
                  <div style={styles.vide}>Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topProduits} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="nom" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip formatter={(v) => [fmtF(parseInt(v)), 'Revenus']} />
                      <Bar dataKey="ca" name="Revenus" radius={[0, 4, 4, 0]}>
                        {topProduits.map((_, i) => (
                          <Cell key={i} fill={COULEURS[i % COULEURS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* VENTES PAR SERVEUR */}
              <div style={styles.graphCard}>
                <h2 style={styles.graphTitre}><LuUser aria-hidden style={{ verticalAlign: '-0.125em' }} /> Ventes par Serveur</h2>
                {ventesServeur.length === 0 ? (
                  <div style={styles.vide}>Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={ventesServeur.map(v => ({ ...v, ca: Number(v.ca) || 0 }))}
                        dataKey="ca"
                        nameKey="nom"
                        cx="50%" cy="45%"
                        innerRadius="45%" outerRadius="75%" paddingAngle={2}
                      >
                        {ventesServeur.map((_, i) => (
                          <Cell key={i} fill={COULEURS[i % COULEURS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => [fmtF(parseInt(v)), 'Revenus']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* VENTES PAR ÉTABLISSEMENT */}
            <div style={styles.graphCard}>
              <h2 style={styles.graphTitre}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Ventes par Établissement</h2>
              {ventesEtab.length === 0 ? (
                <div style={styles.vide}>Aucune donnée</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ventesEtab}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="nom" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} width={52} />
                    <Tooltip formatter={(v) => [fmtF(parseInt(v)), 'Revenus']} />
                    <Legend />
                    <Bar dataKey="ca" name="Revenus (FCFA)" radius={[4, 4, 0, 0]}>
                      {ventesEtab.map((_, i) => (
                        <Cell key={i} fill={COULEURS[i % COULEURS.length]} />
                      ))}
                    </Bar>
                    <Bar dataKey="nombre_ventes" name="Nb Ventes" fill="#E2E8F0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* STOCK EN ALERTE */}
            <div style={styles.graphCard}>
              <h2 style={styles.graphTitre}> Stock en Alerte</h2>
              {stockAlertes.length === 0 ? (
                <div style={{ ...styles.vide, color: '#059669' }}>
                  <LuCircleCheck aria-hidden style={{ verticalAlign: '-0.125em' }} /> Aucun produit en alerte !
                </div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      {['Produit', 'Établissement', 'Stock actuel', 'Seuil alerte', 'Statut'].map((h, i) => (
                        <th key={i} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stockAlertes.map((p, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 700 }}>{p.nom}</span>
                        </td>
                        <td style={styles.td}>{p.etablissement_nom}</td>
                        <td style={styles.td}>
                          <span style={{
                            fontWeight: 700, fontSize: 16,
                            color: p.quantite === 0 ? '#DC2626' : '#EA580C'
                          }}>
                            {p.quantite}
                          </span>
                          <span style={{ color: '#64748B', fontSize: 11 }}>{' ' + p.unite}</span>
                        </td>
                        <td style={styles.td}>{p.seuil_alerte}</td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 700,
                            background: p.quantite === 0 ? '#FEF2F2' : '#FFF7ED',
                            color: p.quantite === 0 ? '#DC2626' : '#EA580C'
                          }}>
                            {p.quantite === 0 ? 'Rupture' : 'Alerte'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  contenu: { ...S.contenu, flex: 1 },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  filtreSelect: { ...S.filtreSelect, minWidth: 200, flex: '1 1 200px', maxWidth: 260 },
  periodeBtns: {
    display: 'flex', background: '#E2E8F0',
    borderRadius: 10, padding: 4, gap: 2, flex: '1 1 280px', maxWidth: 420
  },
  periodeBtn: {
    flex: 1, padding: '8px 10px', borderRadius: 8,
    border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: 13
  },
  chargementInline: {
    textAlign: 'center', padding: 60,
    fontSize: 18, color: '#64748B'
  },
  resumeGrid: { ...S.resumeGrid, marginBottom: 24 },
  resumeCard: S.resumeCard,
  graphCard: {
    background: 'white', borderRadius: 14, minWidth: 0,
    padding: 'clamp(14px, 3vw, 24px)', marginBottom: 24,
    border: '1px solid #E2E8F0', boxShadow: '0 1px 2px rgba(15,23,42,0.04)'
  },
  graphTitre: { fontSize: 16, fontWeight: 700, color: '#0F172A', marginBottom: 20 },
  deuxColonnes: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))', columnGap: 24, marginBottom: 0
  },
  vide: S.vide,
  table: { ...S.table, width: '100%' },
  tableHeader: S.tableHeader,
  th: S.th,
  td: S.td,
};